import { useEffect, useState } from 'react';
import { JOURNEY_NAV_ITEMS, TRACKING_NAV_ITEMS, MOBILE_PRIMARY_ITEMS } from './navItems.js';
import { MainNav } from './MainNav.jsx';
import { AccountMenu } from './AccountMenu.jsx';
import { MobileMoreSheet } from './MobileMoreSheet.jsx';
import { SettingsModal } from '../Settings/SettingsModal.jsx';
import { ErrorBoundary } from '../common/ErrorBoundary.jsx';
import { OfflineNotice } from '../common/OfflineNotice.jsx';

const SIDEBAR_COLLAPSED_KEY = 'cgpt-sidebar-collapsed';

/**
 * Shell compartilhado por TODAS as páginas autenticadas, incluindo o Chat
 * (P23E): sidebar global fixa no desktop (recolhível, estado persistido) e
 * barra de 4 posições no rodapé no mobile (Início, Leitura, Conversar, Mais).
 *
 * A sidebar contém só destinos globais, agrupados por finalidade (Jornada /
 * Acompanhamento) — nunca histórico de conversas, seletor de persona ou
 * "Novo Chat" (isso é específico do Chat e vive num painel contextual à
 * parte, dentro de ChatContainer). Tema, configurações e logout ficam no
 * AccountMenu compacto no rodapé, para que fiquem acessíveis em qualquer
 * página, não só dentro do Chat como antes do P23E.
 */
export const AppShell = ({ children }) => {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true');
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  return (
    <div className="min-h-dvh flex bg-surface text-on-surface">
      <OfflineNotice />
      <aside
        className={`hidden md:flex flex-col flex-shrink-0 bg-surface-container-low border-r border-divider p-3 transition-all duration-200 ${
          collapsed ? 'w-sidebar-collapsed' : 'w-sidebar-expanded'
        }`}
      >
        <div className={`flex items-center mb-4 px-1 ${collapsed ? 'justify-center' : 'justify-between'}`}>
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center border border-outline-variant/20 overflow-hidden shrink-0">
              <img src="/logo-64.webp" alt="CatholicGPT" className="w-5 h-5 object-contain rounded-full" />
            </div>
            {!collapsed && <span className="font-serif italic text-base text-primary tracking-tighter truncate">CatholicGPT</span>}
          </div>
          {!collapsed && (
            <button
              onClick={() => setCollapsed(true)}
              title="Recolher sidebar"
              className="min-h-control-sm min-w-control-sm flex items-center justify-center text-on-surface-variant hover:text-primary hover:bg-primary/5 rounded-lg transition-colors shrink-0"
            >
              <span className="material-symbols-outlined text-[16px]">chevron_left</span>
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto space-y-3">
          <div>
            {!collapsed && (
              <div className="px-2 mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant/50">Jornada</div>
            )}
            <MainNav items={JOURNEY_NAV_ITEMS} collapsed={collapsed} />
          </div>
          <div>
            {!collapsed && (
              <div className="px-2 mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant/50">Acompanhamento</div>
            )}
            <MainNav items={TRACKING_NAV_ITEMS} collapsed={collapsed} />
          </div>
        </div>

        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            title="Expandir sidebar"
            className="mb-2 min-h-control-sm min-w-control-sm mx-auto flex items-center justify-center text-on-surface-variant hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
          >
            <span className="material-symbols-outlined text-[16px]">chevron_right</span>
          </button>
        )}

        <div className="pt-2 border-t border-divider mt-1">
          <AccountMenu collapsed={collapsed} onOpenSettings={() => setIsSettingsOpen(true)} />
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between p-3 pt-safe-top min-h-mobile-header border-b border-outline-variant/10 bg-surface-container-low sticky top-0 z-30">
          <div className="flex items-center gap-2">
            <img src="/logo-64.webp" alt="CatholicGPT" className="w-7 h-7 object-contain rounded-full" />
            <span className="font-serif italic text-primary">CatholicGPT</span>
          </div>
        </header>

        <ErrorBoundary>
          <main className="flex-1 min-w-0 overflow-y-auto pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
            {children}
          </main>
        </ErrorBoundary>

        <div className="md:hidden fixed bottom-0 inset-x-0 pb-safe-bottom bg-surface-container-low border-t border-outline-variant/10 z-40">
          <div className="flex items-stretch justify-between w-full min-h-control">
            <MainNav items={MOBILE_PRIMARY_ITEMS} orientation="horizontal" />
            <button
              onClick={() => setIsMoreOpen(true)}
              aria-label="Mais"
              className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 px-1 text-[11px] text-on-surface/70 hover:text-on-surface transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">more_horiz</span>
              Mais
            </button>
          </div>
        </div>
      </div>

      <MobileMoreSheet open={isMoreOpen} onClose={() => setIsMoreOpen(false)} onOpenSettings={() => setIsSettingsOpen(true)} />
      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
    </div>
  );
};
